/**
 * TypeScript interfaces for Super Admin Onboarding management
 * Extends the base onboarding types with super admin specific functionality
 * 
 * IMPORTANT NAMING CONVENTION:
 * - Field 'restaurant_api_key' is stored in user_onboarding table
 * - When sent to restaurant creation API, it's mapped to 'api_key' field
 * - This maintains compatibility with existing restaurant_api_keys table structure
 */

import { UserOnboardingData, OnboardingStatus } from './onboarding';

// Extended interface for onboarding data with user information and internal fields
export interface SuperAdminOnboardingData extends UserOnboardingData {
  user_name: string;
  user_email: string;
  user_role: string;
  user_created_at: string;
  organisation_name?: string;
  
  // Internal fields for restaurant creation (not exposed to regular users)
  logo_url?: string;
  webhook_secret?: string;
  // NOTE: This field is stored as 'restaurant_api_key' in database but sent as 'api_key' to restaurant creation API
  restaurant_api_key?: string;
  google_oauth_client_id?: string;
  commission_rate?: number | string;
  ubereats_commission_rate?: number | string;
  ubereats_markup?: number | string;
  pickup_commission_rate?: number | string;
  primary_color?: string;
  secondary_color?: string;
  stripe_customer_id?: string;
  opt_in_checkbox_id?: string;
  implicit_consent_enabled?: boolean;
  ordering_page_url?: string;
  review_page_url?: string;
}

// Interface for onboarding table row display
export interface OnboardingTableRow {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_role: string;
  organisation_id?: string;
  restaurant_name?: string;
  organisation_name?: string;
  created_at: string;
  updated_at: string;
  onboarding_status: OnboardingStatus;
  completed_stages: number;
  total_stages: number;
  progress_percentage: number;
  
  // Status fields for each stage
  business_details_status?: OnboardingStatus;
  stripe_connection_status?: OnboardingStatus;
  printer_setup_status?: OnboardingStatus;
  dns_status?: OnboardingStatus;
  website_linked_status?: OnboardingStatus;
  google_ordering_status?: OnboardingStatus;
  uber_aggregation_status?: OnboardingStatus;
  doordash_aggregation_status?: OnboardingStatus;
}

// Search and filter state interface
export interface OnboardingFilters {
  search: string;
  role: string;
  organisation_id?: string;
  status?: OnboardingStatus;
  stage_filter?: string;
}

// Pagination state interface
export interface OnboardingPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// Update payload interface
export interface OnboardingUpdatePayload {
  user_id: string;
  field: keyof UserOnboardingData;
  value: any;
}

// Tab configuration interface
export interface OnboardingTabConfig {
  id: string;
  title: string;
  fields: (keyof UserOnboardingData)[];
  statusField?: keyof UserOnboardingData;
  isRequired: boolean;
  order: number;
}

// Status field mapping
export const STATUS_FIELD_MAP = {
  'business_details': 'business_details_status',
  'stripe_connect': 'stripe_connection_status',
  'printer_setup': 'printer_setup_status',
  'dns_setup': 'dns_status',
  'website_integration': 'website_linked_status',
  'google_business': 'google_ordering_status',
  'uber_aggregation': 'uber_aggregation_status',
  'doordash_aggregation': 'doordash_aggregation_status'
} as const;

// Onboarding stage configuration for super admin
export const ONBOARDING_TABS: OnboardingTabConfig[] = [
  {
    id: 'initial_setup',
    title: 'Initial Setup',
    fields: ['restaurant_name', 'organisation_name', 'address', 'email', 'phone'],
    isRequired: true,
    order: 1
  },
  {
    id: 'business_details',
    title: 'Business Details',
    fields: [
      'restaurant_name', 'organisation_name', 'address', 'email', 'phone',
      'contact_person', 'nzbn', 'company_name', 'trading_name',
      'director_name', 'director_mobile_number', 'gst_number',
      'venue_operating_hours', 'default_preparation_time_minutes',
      'facebook_url', 'instagram_url', 'google_review_url',
      'ubereats_markup', 'primary_color', 'secondary_color'
    ],
    statusField: 'business_details_status',
    isRequired: true,
    order: 2
  },
  {
    id: 'stripe_connect',
    title: 'Stripe Connect',
    fields: ['stripe_connect_link'],
    statusField: 'stripe_connection_status',
    isRequired: true,
    order: 3
  },
  {
    id: 'domain_setup',
    title: 'Domain Setup',
    fields: [
      'dns_setup_method', 'root_domain', 'custom_sub_domain',
      'review_management_subdomain', 'ordering_subdomain_ip',
      'review_management_ip', 'dns_provider_username',
      'dns_provider_password', 'dns_provider_name',
      'dns_provider_custom_name', 'nameserver_1', 'nameserver_2'
    ],
    statusField: 'dns_status',
    isRequired: true,
    order: 4
  },
  {
    id: 'printer_setup',
    title: 'Printer Setup',
    fields: ['printer_setup_method', 'teamviewer_id', 'teamviewer_password'],
    statusField: 'printer_setup_status',
    isRequired: true,
    order: 5
  },
  {
    id: 'website_integration',
    title: 'Website Integration',
    fields: [
      'website_setup_method', 'website_login_username',
      'website_login_password', 'website_platform',
      'website_platform_custom'
    ],
    statusField: 'website_linked_status',
    isRequired: true,
    order: 6
  },
  {
    id: 'google_business',
    title: 'Google My Business',
    fields: ['google_setup_method'],
    statusField: 'google_ordering_status',
    isRequired: true,
    order: 7
  },
  {
    id: 'aggregators',
    title: 'Order Aggregators',
    fields: [
      'uber_aggregation_status', 'uber_setup_method', 'ubereats_store_id',
      'doordash_aggregation_status', 'doordash_setup_method',
      'doordash_login', 'doordash_pin'
    ],
    statusField: 'uber_aggregation_status',
    isRequired: false,
    order: 8
  },
  {
    id: 'restaurant_settings',
    title: 'Restaurant Settings',
    fields: [
      'logo_url', 'template_restaurant_name', 'webhook_secret', 'restaurant_api_key', 'google_oauth_client_id', 'commission_rate',
      'ubereats_commission_rate', 'pickup_commission_rate',
      'stripe_customer_id', 'opt_in_checkbox_id', 'implicit_consent_enabled',
      'ordering_page_url', 'review_page_url'
      // NOTE: 'restaurant_api_key' field gets mapped to 'api_key' when creating restaurants
    ],
    isRequired: false,
    order: 9
  },
  {
    id: 'complete_onboarding',
    title: 'Complete Onboarding',
    fields: [],
    isRequired: true,
    order: 10
  }
];

// Field display configuration
export interface FieldConfig {
  key: keyof UserOnboardingData;
  label: string;
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'number' | 'url';
  placeholder?: string;
  options?: { value: string; label: string; }[];
  required?: boolean;
  description?: string;
  group?: string;
}

// Field groupings for better organization
export const FIELD_GROUPS = {
  basic_info: 'Basic Information',
  business_details: 'Business Details',
  legal_info: 'Legal Information',
  contact_info: 'Contact Information',
  social_media: 'Social Media',
  technical_details: 'Technical Details',
  credentials: 'Access Credentials',
  setup_method: 'Setup Method'
} as const;

// Helper function to calculate onboarding progress
export const calculateOnboardingProgress = (data: SuperAdminOnboardingData): {
  completedStages: number;
  totalStages: number;
  progressPercentage: number;
} => {
  const statusFields = [
    'business_details_status',
    'stripe_connection_status',
    'printer_setup_status',
    'dns_status',
    'website_linked_status',
    'google_ordering_status',
    'uber_aggregation_status',
    'doordash_aggregation_status'
  ] as const;

  const completedStages = statusFields.filter(field => 
    data[field] === OnboardingStatus.COMPLETE
  ).length;
  
  const totalStages = statusFields.length;
  const progressPercentage = Math.round((completedStages / totalStages) * 100);
  
  return {
    completedStages,
    totalStages,
    progressPercentage
  };
};

// Helper function to get status badge variant
export const getStatusBadgeVariant = (status: OnboardingStatus): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case OnboardingStatus.COMPLETE:
      return "default";
    case OnboardingStatus.AWAITING_CONFIRMATION:
      return "secondary";
    case OnboardingStatus.NEEDS_ATTENTION:
      return "destructive";
    default:
      return "outline";
  }
};

// Helper function to get status display name
export const getStatusDisplayName = (status: OnboardingStatus): string => {
  switch (status) {
    case OnboardingStatus.NOT_STARTED:
      return "Not Started";
    case OnboardingStatus.AWAITING_CONFIRMATION:
      return "Awaiting Confirmation";
    case OnboardingStatus.NEEDS_ATTENTION:
      return "Needs Attention";
    case OnboardingStatus.COMPLETE:
      return "Complete";
    default:
      return "Unknown";
  }
};